import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  UserCog,
  Bot,
  MessagesSquare,
  Users,
  CheckCheck,
  Info,
  XCircle,
  GraduationCap,
} from 'lucide-react';
import * as conversationsApi from '@/api/conversations.api';
import * as trainingApi from '@/api/training.api';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { MessageBubble } from './MessageBubble';
import { AgentStatusDot } from './AgentStatusDot';
import { ContactDetailsPanel } from '@/components/contacts/ContactDetailsPanel';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/hooks/useToast';
import { statusBadge } from '@/lib/badges';
import { getContactDisplayName, getContactSubtitle } from '@/lib/contact-display';
import { ordenarMensajes, agruparPorDia } from '@/lib/chat-timeline';
import { Cargando } from '@/components/common/Cargando';

export function ConversationChat({ conversationId }: { conversationId?: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useAuthStore((s) => s.user);
  const [draft, setDraft] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const convo = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => conversationsApi.getConversation(conversationId as string),
    enabled: Boolean(conversationId),
  });

  const messages = useQuery({
    queryKey: ['conversation', conversationId, 'messages'],
    queryFn: () => conversationsApi.listMessages(conversationId as string, { take: 200 }),
    enabled: Boolean(conversationId),
  });

  // Orden por hora REAL de envío (`sentAt`), no por la de ingesta: un mensaje
  // entregado con retraso mostraba y ordenaba por la hora equivocada.
  const ordered = ordenarMensajes(messages.data?.items ?? []);
  const dias = agruparPorDia(ordered);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [ordered.length, conversationId, messages.dataUpdatedAt]);

  const send = useMutation({
    mutationFn: (content: string) =>
      conversationsApi.sendMessage(conversationId as string, content),
    onSuccess: () => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => toast.error('No se pudo enviar el mensaje'),
  });

  const takeControl = useMutation({
    mutationFn: async (toHuman: boolean) => {
      const id = conversationId as string;
      if (toHuman) {
        await conversationsApi.assignUser(id, me?.id ?? null);
        return conversationsApi.updateStatus(id, 'WAITING_HUMAN');
      }
      await conversationsApi.assignUser(id, null);
      return conversationsApi.updateStatus(id, 'ACTIVE');
    },
    onSuccess: (_res, toHuman) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      // Confirmación visible: el cambio de badge solo es demasiado sutil.
      toast.success(
        toHuman ? 'Tomaste el control de la conversación' : 'Conversación devuelta al asistente',
      );
    },
    onError: () => toast.error('No se pudo cambiar el control'),
  });

  // Cerrar conversación (alta A2 de la auditoría): la salida del embudo de
  // "Atención" — sin esto la bandeja nunca queda en cero. El agente NO
  // responde en CLOSED; un mensaje nuevo del cliente la reabre (flujo existente).
  const closeConversation = useMutation({
    mutationFn: () => conversationsApi.updateStatus(conversationId as string, 'CLOSED'),
    onSuccess: () => {
      setConfirmClose(false);
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversación cerrada');
    },
    onError: () => toast.error('No se pudo cerrar la conversación'),
  });

  // Modo entrenamiento: el agente calla y cada respuesta tuya se captura como
  // ejemplo. Al salir, el agente vuelve a responder (la conversación pasa a ACTIVE).
  const training = useMutation({
    mutationFn: (on: boolean) =>
      on
        ? trainingApi.startConversationTraining(conversationId as string)
        : trainingApi.stopConversationTraining(conversationId as string),
    onSuccess: (_res, on) => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      toast.success(
        on
          ? 'Modo entrenamiento: el agente no responde; tus respuestas se capturan como ejemplos'
          : 'Entrenamiento terminado: el agente vuelve a responder',
      );
    },
    onError: () => toast.error('No se pudo cambiar el modo entrenamiento'),
  });

  const markRead = useMutation({
    mutationFn: () => conversationsApi.markRead(conversationId as string),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId, 'messages'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: () => toast.error('No se pudo marcar como leída'),
  });

  // Auto-marcar como leída al abrir la conversación y cada vez que llega un
  // mensaje nuevo mientras está abierta (v0.5). Debounce de 1s: si el unreadCount
  // sube de nuevo, se reprograma; tras marcar, el refetch lo deja en 0 y para.
  const unreadCount = convo.data?.unreadCount ?? 0;
  const markReadRef = useRef(() => {});
  markReadRef.current = () => {
    if (!markRead.isPending) markRead.mutate();
  };
  useEffect(() => {
    if (!conversationId || unreadCount <= 0) return;
    const t = setTimeout(() => markReadRef.current(), 1000);
    return () => clearTimeout(t);
  }, [conversationId, unreadCount]);

  if (!conversationId) {
    return (
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-slate-950">
        <EmptyState
          icon={MessagesSquare}
          title="Selecciona una conversación"
          description="Elige una conversación de la lista para ver los mensajes."
        />
      </div>
    );
  }

  if (convo.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-slate-950">
        <Spinner size={28} />
      </div>
    );
  }

  const c = convo.data;
  const isGroup = c?.contact?.isGroup ?? false;
  const name = getContactDisplayName(c?.contact);
  const badge = c ? statusBadge(c.status) : null;
  const isHumanControlled = c?.status === 'WAITING_HUMAN';
  const channelSuffix = c?.channel?.name ? ` · ${c.channel.name}` : '';
  const subtitle = isGroup
    ? `${c?.contact?.memberCount ? `${c.contact.memberCount} miembros` : 'Grupo'}${channelSuffix}`
    : `${getContactSubtitle(c?.contact)}${channelSuffix}`;

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-x-hidden bg-white dark:bg-slate-950">
      {/* Header */}
      {/* La barra se ACOMODA en pantallas chicas (23-sep-2026): en un iPhone los
          botones en una sola fila estiraban el ancho de toda la página y los
          mensajes quedaban cortados por la derecha, ilegibles. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-surface-border bg-white px-3 py-2 dark:border-white/10 dark:bg-slate-950 lateral:px-4">
        <Avatar
          name={name}
          src={c?.contact?.avatarUrl}
          isGroup={isGroup}
          seed={c?.contact?.externalId}
          size="md"
        />
        <div className="min-w-[10rem] flex-1">
          <p className="flex items-center gap-1.5 truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {isGroup && <Users className="h-4 w-4 shrink-0 text-slate-400" />}
            <span className="truncate">{name}</span>
          </p>
          <p className="truncate text-xs text-slate-400">{subtitle}</p>
        </div>
        {c && !isGroup && <AgentStatusDot conversation={c} />}
        {isGroup && <Badge variant="info">Grupo</Badge>}
        {c?.trainingMode && <Badge variant="warning">Entrenando</Badge>}
        {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setPanelOpen(true)}
          title="Detalles del contacto"
          aria-label="Detalles del contacto"
        >
          <Info className="h-4 w-4" />
        </Button>
        {(c?.unreadCount ?? 0) > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markRead.mutate()}
            loading={markRead.isPending}
            title="Marcar como leída"
          >
            <CheckCheck className="h-4 w-4" /> <span className="hidden sm:inline">Leída</span>
          </Button>
        )}
        {!isGroup && (
          <Button
            variant={c?.trainingMode ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => training.mutate(!c?.trainingMode)}
            loading={training.isPending}
            title={
              c?.trainingMode
                ? 'Terminar el entrenamiento (el agente vuelve a responder)'
                : 'Modo entrenamiento: atiendes tú y tus respuestas se capturan como ejemplos'
            }
          >
            <GraduationCap className="h-4 w-4" />
            <span className="hidden sm:inline">{c?.trainingMode ? 'Terminar' : 'Entrenar'}</span>
          </Button>
        )}
        <Button
          data-guia="chat.tomar-control"
          variant={isHumanControlled ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => takeControl.mutate(!isHumanControlled)}
          loading={takeControl.isPending}
        >
          {isHumanControlled ? (
            <>
              <Bot className="h-4 w-4" /> <span className="hidden sm:inline">Devolver a AI</span>
            </>
          ) : (
            <>
              <UserCog className="h-4 w-4" /> <span className="hidden sm:inline">Tomar control</span>
            </>
          )}
        </Button>
        {c?.status !== 'CLOSED' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmClose(true)}
            title="Cerrar la conversación (un mensaje nuevo del cliente la reabre)"
          >
            <XCircle className="h-4 w-4" /> <span className="hidden sm:inline">Cerrar</span>
          </Button>
        )}
      </div>

      <Modal
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title="Cerrar conversación"
        description="La conversación sale de la bandeja activa y el asistente deja de responderla. Si el cliente vuelve a escribir, se reabre sola."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmClose(false)}>
              Cancelar
            </Button>
            <Button
              loading={closeConversation.isPending}
              onClick={() => closeConversation.mutate()}
            >
              Cerrar conversación
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          ¿Cerrar la conversación con <span className="font-medium">{name}</span>?
        </p>
      </Modal>

      {/* Messages */}
      <div ref={scrollRef} className="min-w-0 flex-1 space-y-2 overflow-y-auto p-3 sm:p-4">
        {messages.isLoading ? (
          <Cargando />
        ) : ordered.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">No hay mensajes todavía.</p>
        ) : (
          dias.map((dia) => (
            <div key={dia.dia} className="space-y-3">
              {/* Separador de día: sin esto, un mensaje de ayer arriba de los de
                  hoy se lee como un error de orden aunque sea correcto. */}
              <div className="flex items-center gap-3 py-1">
                <span className="h-px flex-1 bg-surface-border dark:bg-slate-700" />
                {/* `first-letter:uppercase`, no `capitalize`: en español Intl
                    devuelve "lunes, 15 de junio" y capitalize lo dejaba como
                    "Lunes, 15 De Junio". */}
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-500 first-letter:uppercase dark:bg-slate-800 dark:text-slate-400">
                  {dia.etiqueta}
                </span>
                <span className="h-px flex-1 bg-surface-border dark:bg-slate-700" />
              </div>
              {dia.mensajes.map((m) => (
                <MessageBubble key={m.id} message={m} showSender={c?.contact?.isGroup ?? false} />
              ))}
            </div>
          ))
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) send.mutate(draft.trim());
        }}
        className="flex items-center gap-2 border-t border-surface-border bg-white px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] dark:border-white/10 dark:bg-slate-950"
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe un mensaje…"
          className="h-11 min-w-0 flex-1 rounded-full border-0 bg-white px-4 text-base text-slate-900 caret-acento shadow-[0_0_0_1px_var(--mac-campo-filo)] placeholder:text-slate-400 focus:outline-none focus:ring-[3.5px] focus:ring-acento/50 dark:bg-white/5 dark:text-slate-100 lateral:h-8 lateral:text-sm"
        />
        <Button
          type="submit"
          size="icon"
          className="rounded-full lateral:h-8 lateral:w-8"
          loading={send.isPending}
          disabled={!draft.trim()}
        >
          <Send className="h-4 w-4" />
        </Button>
      </form>

      <ContactDetailsPanel
        contactId={c?.contact?.id ?? c?.contactId}
        conversationId={c?.id ?? conversationId}
        conversationStatus={c?.status}
        leadStatus={c?.leadStatus ?? null}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
      />
    </div>
  );
}
